import React from 'react';
import { useTranslation } from 'react-i18next';

// Import project data
import belleMareData from '../../data/projects/bellemare.js';
import botanicheska302Data from '../../data/projects/botanicheska302.js';
import fifthElementData from '../../data/projects/fifthelement.js';
import gardenData from '../../data/projects/garden.js';
import m1Data from '../../data/projects/m1.js';
import m2Data from '../../data/projects/m2.js';
import paradiseParkData from '../../data/projects/paradisepark.js';
import prestigeParkData from '../../data/projects/prestigepark.js';
import signatureData from '../../data/projects/signature.js';
import theFrameData from '../../data/projects/theframe.js';
import grandParkLivingData from '../../data/projects/grandparkliving.js';
import { getStatusLabel, getTypeLabel } from '../../utils/projectTranslations';

function ProjectsGrid() {
  const { t, i18n } = useTranslation();

  // Get the current language
  const currentLanguage = i18n.language || 'en';

  // Get the location based on current language
  const getLocation = (project) => {
    if (typeof project.location === 'object') {
      return project.location[currentLanguage] || project.location['en'];
    }
    return project.location;
  };

  // Get badge text based on status
  const getBadgeText = (status) => getStatusLabel(status, t);

  // Get free apartments number
  const getFreeApartmentsNumber = (project) => {
    // First check for availableApartments (numeric field)
    if (project.availableApartments !== undefined && project.availableApartments !== null) {
      return project.availableApartments;
    }
    // Fall back to freeApartments (object with translations)
    if (project.freeApartments) {
      let text = '';
      if (typeof project.freeApartments === 'object') {
        text = project.freeApartments[currentLanguage] || project.freeApartments['en'];
      } else {
        text = project.freeApartments;
      }
      // Extract number from text like "15 свободни апартамента" or "15 free apartments"
      const match = text?.match(/\d+/);
      return match ? match[0] : null;
    }
    return null;
  };

  // All projects data
  const allProjects = [
    belleMareData,
    botanicheska302Data,
    fifthElementData,
    gardenData,
    m1Data,
    m2Data,
    paradiseParkData,
    prestigeParkData,
    signatureData,
    theFrameData,
    grandParkLivingData
  ].filter(project => project && project.id);

  return (
    <section className="tc-projects-grid">
      <style>{`
        .tc-projects-grid .project-card-link {
          text-decoration: none !important;
          color: inherit !important;
          display: block !important;
        }
        
        .tc-projects-grid .project-card-link:hover,
        .tc-projects-grid .project-card-link:focus,
        .tc-projects-grid .project-card-link:active {
          text-decoration: none !important;
          color: inherit !important;
        }
        
        .tc-projects-grid .project-card {
          cursor: pointer !important;
        }
        
        .tc-projects-grid .project-link-text {
          transition: transform 0.3s ease;
        }
        
        .tc-projects-grid .project-card:hover .project-link-text {
          transform: translateX(5px);
        }
        
        @keyframes pulse-green {
          0% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.2); opacity: 0.7; }
          100% { transform: scale(1); opacity: 1; }
        }
      `}</style>
      <div className="container">
        <div className="projects-grid">
          <div className="row">
            {allProjects.map((project, index) => (
              <div key={project.id} className="col-lg-4 col-md-6 mb-4">
                <a 
                  href={`/projects/${project.id}`} 
                  className="project-card-link"
                  style={{
                    textDecoration: 'none',
                    color: 'inherit',
                    display: 'block'
                  }}
                >
                  <div className="project-card" style={{
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    height: '100%'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-5px)';
                    e.currentTarget.style.boxShadow = '0 15px 35px rgba(73, 69, 56, 0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.1)';
                  }}>
                    <div className="project-image" style={{ position: 'relative' }}>
                      <img
                        src={project.heroImage}
                        alt={project.title}
                        className="img-cover"
                      />
                      <div className="project-badge">
                        {getBadgeText(project.status)}
                      </div>
                      {getFreeApartmentsNumber(project) && (
                        <div style={{
                          position: 'absolute',
                          top: '60px',
                          right: '15px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: 'transparent',
                          color: 'white',
                          padding: '8px 15px',
                          borderRadius: '20px',
                          fontSize: '0.8rem',
                          fontWeight: '600',
                          boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                          letterSpacing: '0.5px',
                          transition: 'all 0.3s ease',
                          border: '2px solid rgba(255, 255, 255, 0.9)',
                          backdropFilter: 'blur(10px)',
                          zIndex: 2
                        }}>
                          <span style={{
                            display: 'inline-block',
                            width: '8px',
                            height: '8px',
                            backgroundColor: '#4CAF50',
                            borderRadius: '50%',
                            marginRight: '8px',
                            boxShadow: '0 0 8px rgba(76, 175, 80, 0.8)',
                            animation: 'pulse-green 2s infinite',
                            flexShrink: 0
                          }}></span>
                          {getFreeApartmentsNumber(project)}
                        </div>
                      )}
                    </div>
                    <div className="project-info">
                      <div className="project-location">{getLocation(project)}</div>
                      <h3 className="project-name">{project.title}</h3>
                      <div className="project-details">
                        <div className="project-type">
                          <i className="la la-building"></i>
                          <span>{getTypeLabel(project.type, t)}</span>
                        </div>
                        <div className="project-stats">
                          <span>{project.stats.area}</span>
                        </div>
                      </div>
                      <div className="project-link-text" style={{
                        color: 'rgb(73, 69, 56)',
                        fontWeight: '500',
                        marginTop: '15px',
                        display: 'inline-block'
                      }}>
                        {t('projects.viewDetails')} →
                      </div>
                    </div>
                  </div>
                </a>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default ProjectsGrid;
